/**
 * « Enregistrer le gribouillage » : un PNG qui contient le site **et** le
 * dessin (handoff § Fichier).
 *
 * Méthode : le contenu de la toile est cloné dans un `<foreignObject>` SVG, que
 * le navigateur rasterise lui-même. C'est le seul moyen d'obtenir un rendu
 * fidèle — filtres, coins irréguliers, ombres portées — sans embarquer une
 * bibliothèque de rendu DOM, qui serait un demi-mégaoctet de dépendance et une
 * seconde approximation des règles CSS.
 *
 * Deux conditions pour que le SVG soit autonome : la feuille de style doit y
 * être recopiée (le document externe n'est pas visible depuis l'image), et les
 * polices doivent y être en `data:` (aucune requête n'est faite depuis une
 * image). Les deux sont faits ici, et tout échec retombe sur l'export du seul
 * calque de dessin : le visiteur repart toujours avec quelque chose.
 */

/** Polices réellement utilisées par le skin paint. */
const PAINT_FONTS = ['Gloria Hallelujah', 'Patrick Hand'];

async function fontAsDataUrl(url: string): Promise<string | null> {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const buffer = await response.arrayBuffer();
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (const byte of bytes) binary += String.fromCharCode(byte);
    return `data:font/woff2;base64,${btoa(binary)}`;
  } catch {
    return null;
  }
}

/**
 * Recopie les règles CSS du document, en remplaçant les `src:` des polices du
 * skin par leur contenu en `data:`. Les feuilles d'une autre origine sont
 * ignorées : le navigateur en interdit la lecture, et le site n'en a aucune.
 */
async function inlineStyles(): Promise<string> {
  const blocks: string[] = [];
  const fontUrls = new Map<string, string>();

  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList;
    try {
      rules = sheet.cssRules;
    } catch {
      continue;
    }
    for (const rule of Array.from(rules)) {
      const text = rule.cssText;
      if (rule instanceof CSSFontFaceRule) {
        const family = rule.style.getPropertyValue('font-family').replace(/['"]/g, '').trim();
        if (!PAINT_FONTS.includes(family)) continue;
        const source = /url\(["']?([^"')]+)["']?\)/.exec(text)?.[1];
        if (source) fontUrls.set(source, text);
        continue;
      }
      blocks.push(text);
    }
  }

  const faces = await Promise.all(
    Array.from(fontUrls, async ([url, text]) => {
      const data = await fontAsDataUrl(url);
      return data === null ? null : text.replace(url, data);
    }),
  );

  return [...faces.filter((face): face is string => face !== null), ...blocks].join('\n');
}

/**
 * Le contenu d'un `<style>` n'est pas du texte brut en XML : `<` et `&` y
 * seraient lus comme du balisage et rendraient le SVG illisible. Les requêtes
 * de média du site (`@media (width <= 640px)`) en contiennent, d'où ce passage
 * obligé — sans lui, l'image entière échoue en silence.
 */
function escapeForXml(css: string): string {
  return css.replace(/&/g, '&amp;').replace(/</g, '&lt;');
}

export interface RasteriseResult {
  readonly blob: Blob;
  /** Faux quand seul le calque a pu être rendu. */
  readonly withSite: boolean;
}

/**
 * Compose le PNG final : le contenu du site rasterisé, puis le calque de
 * dessin par-dessus.
 */
export async function rasterise(
  content: HTMLElement,
  layer: HTMLCanvasElement,
  background: string,
): Promise<RasteriseResult> {
  const width = Math.max(1, Math.round(content.clientWidth));
  const height = Math.max(1, Math.round(content.clientHeight));
  const ratio = Math.min(2, Math.max(1, window.devicePixelRatio || 1));

  const out = document.createElement('canvas');
  out.width = Math.round(width * ratio);
  out.height = Math.round(height * ratio);
  const ctx = out.getContext('2d');
  if (!ctx) throw new Error('contexte 2D indisponible');
  ctx.scale(ratio, ratio);
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, width, height);

  let withSite = false;
  try {
    const clone = content.cloneNode(true) as HTMLElement;
    clone.style.width = `${String(width)}px`;
    clone.style.margin = '0';

    // Le clone perd ses ancêtres : ce qui était hérité (la police manuscrite,
    // la couleur d'encre, le papier pointillé) doit être recopié sur lui, sinon
    // l'image sort en Times sur fond blanc.
    const inherited = window.getComputedStyle(content);
    clone.style.fontFamily = inherited.fontFamily;
    clone.style.fontSize = inherited.fontSize;
    clone.style.lineHeight = inherited.lineHeight;
    clone.style.color = inherited.color;
    const paper = content.parentElement;
    if (paper) {
      const area = window.getComputedStyle(paper);
      clone.style.background = background;
      clone.style.backgroundImage = area.backgroundImage;
      clone.style.backgroundSize = area.backgroundSize;
    }
    const css = await inlineStyles();
    const serialised = new XMLSerializer().serializeToString(clone);
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="${String(width)}" height="${String(height)}">` +
      `<foreignObject width="100%" height="100%">` +
      `<div xmlns="http://www.w3.org/1999/xhtml">` +
      `<style>${escapeForXml(css)}</style>${serialised}` +
      `</div></foreignObject></svg>`;

    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => {
        resolve(element);
      };
      element.onerror = () => {
        reject(new Error('rendu du site impossible'));
      };
      element.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    });
    ctx.drawImage(image, 0, 0, width, height);
    withSite = true;
  } catch {
    // Un moteur qui refuse le `foreignObject` : on garde le fond et le dessin.
  }

  ctx.drawImage(layer, 0, 0, width, height);

  const blob = await new Promise<Blob | null>((resolve) => {
    out.toBlob(resolve, 'image/png');
  });
  if (!blob) throw new Error('encodage PNG impossible');
  return { blob, withSite };
}

/** Nom de fichier du gribouillage : `steve-2026-08-05-14-32-07.png`. */
export function scribbleFileName(now = new Date()): string {
  const pad = (value: number): string => String(value).padStart(2, '0');
  return [
    'steve-',
    String(now.getFullYear()),
    '-',
    pad(now.getMonth() + 1),
    '-',
    pad(now.getDate()),
    '-',
    pad(now.getHours()),
    '-',
    pad(now.getMinutes()),
    '-',
    pad(now.getSeconds()),
    '.png',
  ].join('');
}

/** Déclenche le téléchargement d'un blob, puis relâche l'URL temporaire. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1000);
}
